/** Windows line endings */
const MOTOR_SYNTH_LINE_BREAK = '\r\n';

/**
 * Based on program.cs
 */
export const convertPreset = (presetContents: string) => {
  return P1_INIT.split('\n')
    .map(line => {
      const [parameter, valueFromDefault] = line.split(';');
      if (parameter && valueFromDefault) {
        const oldParamName = findMappedParameterName(parameter);
        const valueFromOld = findMappedValueInPreset(presetContents, oldParamName ?? '');
        const newLine = valueFromOld
          ? `${parameter};${getMappedValueIdDefined(valueFromOld, parameter, presetContents)}`
          : `${parameter};${valueFromDefault}`;
        return newLine;
      }
      return undefined;
    })
    .filter(line => line !== undefined)
    .join(MOTOR_SYNTH_LINE_BREAK) + MOTOR_SYNTH_LINE_BREAK;
}

const findMappedParameterName = (parameter: string): string | undefined => {
  const lines = NAMEMAP_NOMOD_CSV
    .split('\n')
    .splice(1); // skip header
  for (const line of lines) {
    const [newName, oldName, mapped] = line.split(';');
    if (parameter === newName && ['y', 'v'].includes(mapped)) {
      return oldName;
    }
  }
}

const findMappedValueInPreset = (presetContents: string, oldParamName: string): string | undefined => {
  if (!oldParamName) return;
  const lines = presetContents.split('\n');
  for (const line of lines) {
    const [parameter, foundVal] = line.split(';');
    if (oldParamName === parameter) {
      return foundVal;
    }
  }
}

const getMappedValueIdDefined = (valueFromOld: string, parameter: string, presetContents: string): string => {
  const foundValue = valueFromOld;
  const lines = NAMEMAP_NOMOD_CSV
    .split('\n')
    .splice(1);  // skip header
  for (const line of lines) {
    const [newName, oldName, mapped, valueCoef] = line.split(';');
    if (parameter === newName) {
      if (mapped === 'y' && valueCoef) {
        // Multiply old value to get new from coef.
        const updatedValue = Number.parseFloat(valueFromOld) * Number.parseFloat(valueCoef);
        return formatDouble(updatedValue); // e.g. 1.23456
      } else if (mapped === 'v' && valueCoef) {
        return runParamConversion(valueFromOld, valueCoef, presetContents);
      }
    }
  }

  return foundValue;
}

const runParamConversion = (valueFromOld: string, valueCoef: string, presetContents: string) => {
  let foundValueStr = valueFromOld;
  switch (valueCoef) {
    case "vcfFc":
      foundValueStr = formatDouble(12000 * Number.parseFloat(valueFromOld));
      const keytrOnStr = findMappedValueInPreset(presetContents, "P_KEYTRACK");
      if (keytrOnStr) {
        const keytrOn = Number.parseFloat(keytrOnStr);
        const keytrBaseStr = findMappedValueInPreset(presetContents, "P_KEYTRACK_TUNE");
        if (keytrOn > 0) {
          if (keytrBaseStr) {
            //Replace with keytrack tune val if keytrack on
            foundValueStr = formatDouble(clipMinMax(Number.parseFloat(keytrBaseStr), 0, 12000));
          }
        }
        if (keytrOn > 1) {   //TARGET mode
          foundValueStr = "0.00000";
        }
      }

      break;

    case "log2(1.01 + P_LFO_RATE)":
      foundValueStr = formatDouble(Math.log2(1.01 + Number.parseFloat(valueFromOld)));
      break;

    case "* !P_LFO_V1_FREQ_AMP":
    case "* P_LFO_V1_FREQ_AMP":
      const lfoV1stateStr = findMappedValueInPreset(presetContents, "P_LFO_V1_FREQ_AMP");
      if (lfoV1stateStr) {
        let lfoV1state = Number.parseFloat(lfoV1stateStr);
        if (valueCoef === "* !P_LFO_V1_FREQ_AMP") {
          lfoV1state = 1 - lfoV1state;
        }
        foundValueStr = formatDouble(Number.parseFloat(valueFromOld) * lfoV1state);
      }
      break;

    case "* !P_LFO_CF_RES":
    case "* P_LFO_CF_RES":
      const lfFcstateStr = findMappedValueInPreset(presetContents, "P_LFO_CF_RES");
      if (lfFcstateStr) {
        let lfoFcstate = Number.parseFloat(lfFcstateStr);
        if (valueCoef === "* !P_LFO_CF_RES") {
          lfoFcstate = 1 - lfoFcstate;
        }
        foundValueStr = formatDouble(Number.parseFloat(valueFromOld) * lfoFcstate);
      }
      break;

    case "* !P_LFO_V2_FREQ_AMP":
    case "* P_LFO_V2_FREQ_AMP":
      const lfoV2stateStr = findMappedValueInPreset(presetContents, "P_LFO_V2_FREQ_AMP");
      if (lfoV2stateStr) {
        let lfoV2state = Number.parseFloat(lfoV2stateStr);
        if (valueCoef === "* !P_LFO_V2_FREQ_AMP") {
          lfoV2state = 1 - lfoV2state;
        }
        foundValueStr = formatDouble(Number.parseFloat(valueFromOld) * lfoV2state);
      }
      break;

    case "0 if 0, 3 if 1, 4 if 2":
      const val1 = Number.parseInt(valueFromOld);
      if (0 === val1) { foundValueStr = "0.00000"; }
      else if (1 === val1) { foundValueStr = "3.00000"; }
      else if (2 === val1) { foundValueStr = "4.00000"; }
      break;

    case "1 if 0, 3 if 1, 4 if 2":
      const val2 = Number.parseInt(valueFromOld);
      if (0 === val2) { foundValueStr = "1.00000"; }
      else if (1 === val2) { foundValueStr = "3.00000"; }
      else if (2 === val2) { foundValueStr = "4.00000"; }
      break;

    case "2 if 0, 3 if 1, 4 if 2":
      const val3 = Number.parseInt(valueFromOld);
      if (0 === val3) { foundValueStr = "2.00000"; }
      else if (1 === val3) { foundValueStr = "3.00000"; }
      else if (2 === val3) { foundValueStr = "4.00000"; }
      break;

    case "x - 1, clip 0 - 3":
      foundValueStr = formatDouble(clipMinMax(Number.parseFloat(valueFromOld) - 1, 0, 3));
      break;

    case "x - 2, clip 0 - 6":
      foundValueStr = formatDouble(clipMinMax(Number.parseFloat(valueFromOld) - 2, 0, 6));
      break;

    case "from/to":
      let fromTo = Number.parseFloat(valueFromOld);
      if ((fromTo < -12) || (fromTo > 12)) {
        fromTo = 0;
      }
      foundValueStr = formatDouble(clipMinMax(fromTo, -12, 12));
      break;

    case "accel":
      let accel = Number.parseFloat(valueFromOld);
      if (accel < 0.01) { accel = 0; }
      accel *= 0.25;
      foundValueStr = formatDouble(clipMinMax(accel, 0, 1));
      break;

    case "brake":
      let brake = Number.parseFloat(valueFromOld);
      brake *= 5;
      if (brake < 0.01) { brake = 0; }
      foundValueStr = formatDouble(clipMinMax(brake, 0, 1));
      break;

    case "accelCurve":
      let accCurve = Number.parseFloat(valueFromOld);
      accCurve *= -1;
      foundValueStr = formatDouble(clipMinMax(accCurve, -1, 1));
      break;

    case "P_VOICE1_MODE || P_VOICE2_MODE":
      const v1Mode = Number.parseInt(findMappedValueInPreset(presetContents, "P_VOICE1_MODE")!);
      const v2Mode = Number.parseInt(findMappedValueInPreset(presetContents, "P_VOICE2_MODE")!);
      if ((v1Mode > 0) || (v2Mode > 0)) {
        foundValueStr = "1.00000";
      }
      else {
        foundValueStr = "0.00000";
      }
      break;

    case "clip -2 - 2":
      foundValueStr = formatDouble(clipMinMax(Number.parseFloat(valueFromOld), -2, 2));
      break;

    case "x/2 + 0.5":
      foundValueStr = formatDouble(clipMinMax((Number.parseFloat(valueFromOld) * 0.5) + 0.5, 0, 1));
      break;

    case "fm/am":
      let fmAm = !!Number.parseInt(valueFromOld); // !! converts int to bool
      fmAm = !fmAm;
      foundValueStr = formatDouble(+fmAm);  // + converts bool to int
      break;
  }
  return foundValueStr;
}

/** e.g. 100.23456 */
const formatDouble = (value: number) => value.toFixed(5);

/**
 * -5, [0,10] => 0
 * 20, [0, 10] => 10
 * 5, [0,10] => 5
 */
const clipMinMax = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

/**
 * Default preset file contents, used in conversion process
 */
const P1_INIT = `
PRESET_FILE_V;1.2
P_V1_VOL;1.00000
P_V2_VOL;1.00000
P_MOTOR_VOL;1.00000
P_DCO_VOL;1.00000
P_DCO_PW;0.00000
P_DCO_FOLD;0.00000
P_DCO_NOISE_GRAIN;0.00000
P_DCO_NOISE_SAMPLE;0.00000
P_DCO_ROUTE;0.00000
P_CLEAN_VOL;0.00000
P_DRIVE_V1;0.00000
P_DRIVE_V2;0.00000
P_DRIVE_DCO;0.00000
P_VCF_RES_V1;0.00000
P_VCF_RES_V2;0.00000
P_VCF_RES_DCO;0.00000
P_VCF_FC_V1;12000.00000
P_VCF_FC_V2;12000.00000
P_VCF_FC_DCO;12000.00000
P_VCF_EG_V1;0.00000
P_VCF_EG_V2;0.00000
P_VCF_EG_DCO;0.00000
P_V1_DETUNE;0.00000
P_V2_DETUNE;0.00000
P_DCO_DETUNE;0.00000
P_DETUNE_MIN;-12.00000
P_DETUNE_MAX;12.00000
P_OSC_DETUNE;0.00000
P_CROSS_MOD;0.00000
P_ALL_FREQ;0.00000
P_V1_ACCEL;0.00000
P_V1_BRAKE;0.00000
P_V1_ACCEL_FROM;-5.00000
P_V1_BRAKE_TO;5.00000
P_V1_ACCEL_CURVE;0.00000
P_V1_ACCEL_MODE;0.00000
P_V2_ACCEL;0.00000
P_V2_BRAKE;0.00000
P_V2_ACCEL_FROM;-5.00000
P_V2_BRAKE_TO;5.00000
P_V2_ACCEL_CURVE;0.00000
P_V2_ACCEL_MODE;0.00000
P_DCO_ACCEL;0.00000
P_DCO_BRAKE;0.00000
P_DCO_ACCEL_FROM;-5.00000
P_DCO_BRAKE_TO;5.00000
P_DCO_ACCEL_CURVE;0.00000
P_DCO_ACCEL_MODE;0.00000
P_V1_WAVE;2.00000
P_V2_WAVE;2.00000
P_DCO_WAVE;0.00000
P_VCF_TYPE_V1;0.00000
P_VCF_TYPE_V2;0.00000
P_VCF_TYPE_DCO;0.00000
P_V1_SCALE;1.00000
P_V2_SCALE;1.00000
P_DCO_SCALE;1.00000
P_KEYTR_MODE_V1;0.00000
P_KEYTR_MODE_V2;0.00000
P_KEYTR_MODE_DCO;0.00000
P_KEYTR_AMNT_V1;1.00000
P_KEYTR_AMNT_V2;1.00000
P_KEYTR_AMNT_DCO;1.00000
P_KEYTR_GLIDE_V1;0.00000
P_KEYTR_GLIDE_V2;0.00000
P_KEYTR_GLIDE_DCO;0.00000
P_V1_ENV_TYPE;0.00000
P_V1_DELAY;0.00000
P_V1_ATTACK;0.00500
P_V1_DECAY;0.20000
P_V1_SUSTAIN;0.70000
P_V1_HOLD;0.00000
P_V1_RELEASE;2.00000
P_V1_ATT_CURVE;0.75000
P_V1_DEC_CURVE;0.75000
P_V1_SUS_TIME;0.00000
P_V1_REL_CURVE;0.75000
P_V1_ENV_RST;0.00000
P_V1_VEL_SCALE;1.00000
P_V2_ENV_TYPE;0.00000
P_V2_DELAY;0.00000
P_V2_ATTACK;0.00500
P_V2_DECAY;0.20000
P_V2_SUSTAIN;0.70000
P_V2_HOLD;0.00000
P_V2_RELEASE;2.00000
P_V2_ATT_CURVE;0.75000
P_V2_DEC_CURVE;0.75000
P_V2_SUS_TIME;0.00000
P_V2_REL_CURVE;0.75000
P_V2_ENV_RST;0.00000
P_V2_VEL_SCALE;1.00000
P_DCO_ENV_TYPE;0.00000
P_DCO_DELAY;0.00000
P_DCO_ATTACK;0.00500
P_DCO_DECAY;0.20000
P_DCO_SUSTAIN;0.70000
P_DCO_HOLD;0.00000
P_DCO_RELEASE;2.00000
P_DCO_ATT_CURVE;0.75000
P_DCO_DEC_CURVE;0.75000
P_DCO_SUS_TIME;0.00000
P_DCO_REL_CURVE;0.75000
P_DCO_ENV_RST;0.00000
P_DCO_VEL_SCALE;1.00000
P_VCF_V1_ENV_TYPE;0.00000
P_VCF_V1_DELAY;0.00000
P_VCF_V1_ATTACK;0.00500
P_VCF_V1_DECAY;0.20000
P_VCF_V1_SUSTAIN;1.00000
P_VCF_V1_HOLD;0.00000
P_VCF_V1_RELEASE;3.00000
P_VCF_V1_ATT_CURVE;0.75000
P_VCF_V1_DEC_CURVE;0.75000
P_VCF_V1_SUS_TIME;0.00000
P_VCF_V1_REL_CURVE;0.75000
P_VCF_V1_ENV_RST;0.00000
P_VCF_V1_VEL_SCALE;0.00000
P_VCF_V2_ENV_TYPE;0.00000
P_VCF_V2_DELAY;0.00000
P_VCF_V2_ATTACK;0.00500
P_VCF_V2_DECAY;0.20000
P_VCF_V2_SUSTAIN;1.00000
P_VCF_V2_HOLD;0.00000
P_VCF_V2_RELEASE;3.00000
P_VCF_V2_ATT_CURVE;0.75000
P_VCF_V2_DEC_CURVE;0.75000
P_VCF_V2_SUS_TIME;0.00000
P_VCF_V2_REL_CURVE;0.75000
P_VCF_V2_ENV_RST;0.00000
P_VCF_V2_VEL_SCALE;0.00000
P_VCF_DCO_ENV_TYPE;0.00000
P_VCF_DCO_DELAY;0.00000
P_VCF_DCO_ATTACK;0.00500
P_VCF_DCO_DECAY;0.20000
P_VCF_DCO_SUSTAIN;1.00000
P_VCF_DCO_HOLD;0.00000
P_VCF_DCO_RELEASE;3.00000
P_VCF_DCO_ATT_CURVE;0.75000
P_VCF_DCO_DEC_CURVE;0.75000
P_VCF_DCO_SUS_TIME;0.00000
P_VCF_DCO_REL_CURVE;0.75000
P_VCF_DCO_ENV_RST;0.00000
P_VCF_DCO_VEL_SCALE;0.00000
P_UNLINK_MODE;0.00000
P_ACCEL_LINK;0.00000
P_ENV_LINK;0.00000
P_FILTER_LINK;0.00000
P_MOD_RATE_LFO1;0.50000
P_MOD_RATE_LFO2;1.00000
P_MOD_RATE_LFO3;2.00000
P_MOD1_SUBDIV_UP;0.00000
P_MOD1_SUBDIV_DOWN;3.00000
P_MOD2_SUBDIV_UP;0.00000
P_MOD2_SUBDIV_DOWN;3.00000
P_MOD3_SUBDIV_UP;0.00000
P_MOD3_SUBDIV_DOWN;3.00000
P_MOD1_ACT_SLOT;0.00000
P_MOD2_ACT_SLOT;0.00000
P_MOD3_ACT_SLOT;0.00000
P_MOD_DEPTH_1A;0.00000
P_MOD_DEPTH_1B;0.00000
P_MOD_DEPTH_2A;0.00000
P_MOD_DEPTH_2B;0.00000
P_MOD_DEPTH_3A;0.00000
P_MOD_DEPTH_3B;0.00000
P_MOD1_SHAPE;1.00000
P_MOD2_SHAPE;1.00000
P_MOD3_SHAPE;1.00000
P_MOD1_SLOPE;0.20000
P_MOD2_SLOPE;0.20000
P_MOD3_SLOPE;0.20000
P_MOD1_X_OFFS_STEP;0.00000
P_MOD1_X_OFFS_TRI;0.00000
P_MOD1_X_OFFS_SQ;0.00000
P_MOD1_X_OFFS_SAW_UP;0.00000
P_MOD1_X_OFFS_SAW_DOWN;0.00000
P_MOD1_Y_OFFS_STEP;0.00000
P_MOD1_Y_OFFS_TRI;0.00000
P_MOD1_Y_OFFS_SQ;0.00000
P_MOD1_Y_OFFS_SAW_UP;0.00000
P_MOD1_Y_OFFS_SAW_DOWN;0.00000
P_MOD2_X_OFFS_STEP;0.00000
P_MOD2_X_OFFS_TRI;0.00000
P_MOD2_X_OFFS_SQ;0.00000
P_MOD2_X_OFFS_SAW_UP;0.00000
P_MOD2_X_OFFS_SAW_DOWN;0.00000
P_MOD2_Y_OFFS_STEP;0.00000
P_MOD2_Y_OFFS_TRI;0.00000
P_MOD2_Y_OFFS_SQ;0.00000
P_MOD2_Y_OFFS_SAW_UP;0.00000
P_MOD2_Y_OFFS_SAW_DOWN;0.00000
P_MOD3_X_OFFS_STEP;0.00000
P_MOD3_X_OFFS_TRI;0.00000
P_MOD3_X_OFFS_SQ;0.00000
P_MOD3_X_OFFS_SAW_UP;0.00000
P_MOD3_X_OFFS_SAW_DOWN;0.00000
P_MOD3_Y_OFFS_STEP;0.00000
P_MOD3_Y_OFFS_TRI;0.00000
P_MOD3_Y_OFFS_SQ;0.00000
P_MOD3_Y_OFFS_SAW_UP;0.00000
P_MOD3_Y_OFFS_SAW_DOWN;0.00000
P_MOD1_CURVE_TRI;0.00000
P_MOD1_PW_PWM;0.00000
P_MOD1_CURVE_SAW_UP;0.00000
P_MOD1_CURVE_SAW_DOWN;0.00000
P_MOD2_CURVE_TRI;0.00000
P_MOD2_PW_PWM;0.00000
P_MOD2_CURVE_SAW_UP;0.00000
P_MOD2_CURVE_SAW_DOWN;0.00000
P_MOD3_CURVE_TRI;0.00000
P_MOD3_PW_PWM;0.00000
P_MOD3_CURVE_SAW_UP;0.00000
P_MOD3_CURVE_SAW_DOWN;0.00000
P_STEP_MOD1_TYPE;0.00000
P_STEP_MOD2_TYPE;0.00000
P_STEP_MOD3_TYPE;0.00000
P_STEP_MOD1_STEPS;4.00000
P_STEP_MOD2_STEPS;4.00000
P_STEP_MOD3_STEPS;4.00000
P_STEP_MOD1_PATT;0.00000
P_STEP_MOD2_PATT;0.00000
P_STEP_MOD3_PATT;0.00000
P_NOISE_MOD1_GRAIN;0.00000
P_NOISE_MOD2_GRAIN;0.00000
P_NOISE_MOD3_GRAIN;0.00000
P_MOD1_ENV_TYPE;0.00000
P_MOD1_DELAY;0.00000
P_MOD1_ATTACK;0.00500
P_MOD1_DECAY;0.20000
P_MOD1_SUSTAIN;1.00000
P_MOD1_HOLD;0.00000
P_MOD1_RELEASE;20.00000
P_MOD1_ATT_CURVE;0.75000
P_MOD1_DEC_CURVE;0.75000
P_MOD1_SUS_TIME;0.00000
P_MOD1_REL_CURVE;0.75000
P_MOD1_ENV_RST;1.00000
P_MOD2_ENV_TYPE;0.00000
P_MOD2_DELAY;0.00000
P_MOD2_ATTACK;0.00500
P_MOD2_DECAY;0.20000
P_MOD2_SUSTAIN;1.00000
P_MOD2_HOLD;0.00000
P_MOD2_RELEASE;20.00000
P_MOD2_ATT_CURVE;0.75000
P_MOD2_DEC_CURVE;0.75000
P_MOD2_SUS_TIME;0.00000
P_MOD2_REL_CURVE;0.75000
P_MOD2_ENV_RST;1.00000
P_MOD3_ENV_TYPE;0.00000
P_MOD3_DELAY;0.00000
P_MOD3_ATTACK;0.00500
P_MOD3_DECAY;0.20000
P_MOD3_SUSTAIN;1.00000
P_MOD3_HOLD;0.00000
P_MOD3_RELEASE;20.00000
P_MOD3_ATT_CURVE;0.75000
P_MOD3_DEC_CURVE;0.75000
P_MOD3_SUS_TIME;0.00000
P_MOD3_REL_CURVE;0.75000
P_MOD3_ENV_RST;1.00000
P_MOD1_DST_A;5.00000
P_MOD1_DST_B;1.00000
P_MOD2_DST_A;8.00000
P_MOD2_DST_B;12.00000
P_MOD3_DST_A;6.00000
P_MOD3_DST_B;2.00000
P_MOD1_DST_PARAM_A;0.00000
P_MOD1_DST_PARAM_B;0.00000
P_MOD2_DST_PARAM_A;0.00000
P_MOD2_DST_PARAM_B;0.00000
P_MOD3_DST_PARAM_A;0.00000
P_MOD3_DST_PARAM_B;0.00000
P_MOD1_CLK_SRC;3.00000
P_MOD2_CLK_SRC;3.00000
P_MOD3_CLK_SRC;3.00000
P_MOD1_PHASE_RST;0.00000
P_MOD2_PHASE_RST;0.00000
P_MOD3_PHASE_RST;0.00000
P_V1_POLY;2.00000
P_V2_POLY;2.00000
P_DCO_POLY;2.00000
P_PWH_R_LOW;-2.00000
P_PWH_R_HI;2.00000
P_MODW_ASSIGN_1;32.00000
P_MODW_ASSIGN_2;0.00000
P_MODW_ASSIGN_3;0.00000
P_MODW_SCALE_1;-1.00000
P_MODW_SCALE_2;1.00000
P_MODW_SCALE_3;1.00000
P_CH_PRES_ASSIGN_V1;0.00000
P_CH_PRES_ASSIGN_V2;0.00000
P_CH_PRES_ASSIGN_DCO;0.00000
P_CH_PR_SCALE_V1;0.50000
P_CH_PR_SCALE_V2;0.50000
P_CH_PR_SCALE_DCO;0.50000
P_ARP_V1;1.00000
P_ARP_V2;1.00000
P_ARP_DCO;1.00000
P_ARP_MODE;0.00000
P_ARP_TIME_DIV;2.00000
P_ARP_GATE_LEN;0.50000
P_ARP_SWING;0.50000
P_ARP_NOTE_REPEAT;0.00000
P_ARP_PATTERN_LEN;7.00000
P_ARP_ACCENT_PAT;0.00000
P_ARP_PATT_SHIFT;0.00000
P_ARP_PATT_REP;0.00000
P_KEYS_ROOT;4.00000
P_KEYS_SCALE;4.00000
P_KEYS_STEP;0.00000
P_KEY_C_B1;3600.00000
P_KEY_C_B2;3900.00000
P_KEY_C_B3;4100.00000
P_KEY_C_B4;4300.00000
P_KEY_C_B5;4600.00000
P_KEY_C_B6;4800.00000
P_KEY_C_B7;5100.00000
P_KEY_C_B8;5300.00000
P_VOC_LSB;3.21890
P_VOC_RSB;9.61580
P_VOC_Q;0.00000
P_VOC_FC;1.00000
P_VOC_BLEND;0.00000
P_VOC_BW;0.50000
P_VOC_FILT_COUNT;1.00000
P_VOC_ON;0.00000
P_VOC_IN_GAIN;0.00000
P_OSC_DRIFT_RATE;0.00000
P_DETUNE_CTRL_2;0.00000
P_DETUNE_CTRL_3;0.00000
P_DETUNE_CTRL_4;0.00000
P_CROSS_V1;1.00000
P_CROSS_V2;1.00000
P_CROSS_DCO;1.00000
P_CROSS_PW;1.00000
P_CROSS_SCALE;2.00000
P_DCO_HARDYNC_STATE;0.00000
P_DCO_HARDYNC_TO;0.00000
P_TONE_MODE;0.00000
P_MAN_TONE_V1;0.50000
P_MAN_TONE_V2;0.50000
P_RND_VOICE_EN;1.00000
P_RND_D_WAVE;2.00000
P_RND_D_AMP_ENV;2.00000
P_RND_D_ACCEL;0.00000
P_RND_D_VCF;2.00000
P_RND_D_DRIVE;0.00000
P_RND_D_NOTES;0.00000
P_RND_D_DETUNE;0.00000
P_RND_D_MOD1;2.00000
P_RND_D_MOD2;2.00000
P_RND_D_MOD3;2.00000
P_RND_D_MOD_DST;0.00000
P_RND_D_CROSS;0.00000
P_RND_D_ARP;2.00000
P_RND_D_POLY;0.00000
P_V1_SUS_BREAK;0.00000
P_V2_SUS_BREAK;0.00000
P_DCO_SUS_BREAK;0.00000
P_VCF_V1_SUS_BREAK;0.00000
P_VCF_V2_SUS_BREAK;0.00000
P_VCF_DCO_SUS_BREAK;0.00000
P_MOD1_SUS_BREAK;0.00000
P_MOD2_SUS_BREAK;0.00000
P_MOD3_SUS_BREAK;0.00000
P_MOD1_VEL_SCALE;0.00000
P_MOD2_VEL_SCALE;0.00000
P_MOD3_VEL_SCALE;0.00000
P_ARP_OCT_RANGE;0.00000
P_DETUNE_EN_V1;1.00000
P_DETUNE_EN_V2;1.00000
P_DETUNE_EN_DCO;1.00000
P_ARP_GLOBAL_ON;0.00000
P_MOD1A_SCALE;1.00000
P_MOD2A_SCALE;1.00000
P_MOD3A_SCALE;1.00000
P_MOD1B_SCALE;1.00000
P_MOD2B_SCALE;1.00000
P_MOD3B_SCALE;1.00000
P_V1_FILT;1.00000
P_V2_FILT;1.00000
P_FM_AM;1.00000
CUSTOM_DATA_STARTS;1
ARP_RYTHM_LEN;8.00000
ARP_RYTHM_STEP;0,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;1,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;2,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;3,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;4,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;5,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;6,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;7,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;8,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;9,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;10,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;11,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;12,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;13,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;14,1.00000,0.00000,1.00000,1.00000,0
ARP_RYTHM_STEP;15,1.00000,0.00000,1.00000,1.00000,0
`.trim();

const NAMEMAP_NOMOD_CSV = `
NEW_NAME;OLD_NAME;mapped;valueCoef
PRESET_FILE_V;;n;
P_V1_VOL;P_V1_VOL;y;
P_V2_VOL;P_V2_VOL;y;
P_MOTOR_VOL;;n;
P_DCO_VOL;;n;
P_DCO_PW;;n;
P_DCO_FOLD;;n;
P_DCO_NOISE_GRAIN;;n;
P_DCO_NOISE_SAMPLE;;n;
P_DCO_ROUTE;;n;
P_CLEAN_VOL;;n;
P_DRIVE_V1;P_DRIVE;y;0.003921569
P_DRIVE_V2;P_DRIVE;y;0.003921569
P_DRIVE_DCO;P_DRIVE;y;0.003921569
P_VCF_RES_V1;P_VCF_RES;y;
P_VCF_RES_V2;P_VCF_RES;y;
P_VCF_RES_DCO;P_VCF_RES;y;
P_VCF_FC_V1;P_VCF_FC;v;vcfFc
P_VCF_FC_V2;P_VCF_FC;v;vcfFc
P_VCF_FC_DCO;P_VCF_FC;v;vcfFc
P_VCF_EG_V1;P_VCF_EG;y;
P_VCF_EG_V2;P_VCF_EG;y;
P_VCF_EG_DCO;P_VCF_EG;y;
P_V1_DETUNE;;n;
P_V2_DETUNE;P_V2_DETUNE;y;
P_DCO_DETUNE;;n;
P_DETUNE_MIN;;n;
P_DETUNE_MAX;;n;
P_OSC_DETUNE;;n;
P_CROSS_MOD;P_CROSS;y;0.25
P_ALL_FREQ;;n;
P_V1_ACCEL;P_V1_ACCEL;v;accel
P_V1_BRAKE;P_V1_BRAKE;v;brake
P_V1_ACCEL_FROM;P_ACCEL_START;v;from/to
P_V1_BRAKE_TO;P_ACCEL_END;v;from/to
P_V1_ACCEL_CURVE;P_ACCEL_CURVE;v;accelCurve
P_V1_ACCEL_MODE;;n;
P_V2_ACCEL;P_V2_ACCEL;v;accel
P_V2_BRAKE;P_V2_BRAKE;v;brake
P_V2_ACCEL_FROM;P_ACCEL_START;v;from/to
P_V2_BRAKE_TO;P_ACCEL_END;v;from/to
P_V2_ACCEL_CURVE;P_ACCEL_CURVE;v;accelCurve
P_V2_ACCEL_MODE;;n;
P_DCO_ACCEL;;n;
P_DCO_BRAKE;;n;
P_DCO_ACCEL_FROM;;n;
P_DCO_BRAKE_TO;;n;
P_DCO_ACCEL_CURVE;;n;
P_DCO_ACCEL_MODE;;n;
P_V1_WAVE;P_V1_WAVE;y;
P_V2_WAVE;P_V2_WAVE;y;
P_DCO_WAVE;;n;
P_VCF_TYPE_V1;P_VCF_TYPE;y;
P_VCF_TYPE_V2;;n;
P_VCF_TYPE_DCO;;n;
P_V1_SCALE;P_V1_SCALE;y;
P_V2_SCALE;P_V2_SCALE;y;
P_DCO_SCALE;;n;
P_KEYTR_MODE_V1;P_KEYTRACK;y;
P_KEYTR_MODE_V2;;n;
P_KEYTR_MODE_DCO;;n;
P_KEYTR_AMNT_V1;;n;
P_KEYTR_AMNT_V2;;n;
P_KEYTR_AMNT_DCO;;n;
P_KEYTR_GLIDE_V1;;n;
P_KEYTR_GLIDE_V2;;n;
P_KEYTR_GLIDE_DCO;;n;
P_V1_ENV_TYPE;;n;
P_V1_DELAY;;n;
P_V1_ATTACK;P_V1_ATTACK;y;
P_V1_DECAY;P_V1_DECAY;y;
P_V1_SUSTAIN;P_V1_SUSTAIN;y;
P_V1_HOLD;;n;
P_V1_RELEASE;P_V1_RELEASE;y;
P_V1_ATT_CURVE;;n;
P_V1_DEC_CURVE;;n;
P_V1_SUS_TIME;;n;
P_V1_REL_CURVE;;n;
P_V1_ENV_RST;;n;
P_V1_VEL_SCALE;;n;
P_V2_ENV_TYPE;;n;
P_V2_DELAY;;n;
P_V2_ATTACK;P_V2_ATTACK;y;
P_V2_DECAY;P_V2_DECAY;y;
P_V2_SUSTAIN;P_V2_SUSTAIN;y;
P_V2_HOLD;;n;
P_V2_RELEASE;P_V2_RELEASE;y;
P_V2_ATT_CURVE;;n;
P_V2_DEC_CURVE;;n;
P_V2_SUS_TIME;;n;
P_V2_REL_CURVE;;n;
P_V2_ENV_RST;;n;
P_V2_VEL_SCALE;;n;
P_DCO_ENV_TYPE;;n;
P_DCO_DELAY;;n;
P_DCO_ATTACK;;n;
P_DCO_DECAY;;n;
P_DCO_SUSTAIN;;n;
P_DCO_HOLD;;n;
P_DCO_RELEASE;;n;
P_DCO_ATT_CURVE;;n;
P_DCO_DEC_CURVE;;n;
P_DCO_SUS_TIME;;n;
P_DCO_REL_CURVE;;n;
P_DCO_ENV_RST;;n;
P_DCO_VEL_SCALE;;n;
P_VCF_V1_ENV_TYPE;;n;
P_VCF_V1_DELAY;;n;
P_VCF_V1_ATTACK;P_FILTER_ATTACK;y;
P_VCF_V1_DECAY;;n;
P_VCF_V1_SUSTAIN;;n;
P_VCF_V1_HOLD;;n;
P_VCF_V1_RELEASE;P_FILTER_RELEASE;y;
P_VCF_V1_ATT_CURVE;;n;
P_VCF_V1_DEC_CURVE;;n;
P_VCF_V1_SUS_TIME;;n;
P_VCF_V1_REL_CURVE;;n;
P_VCF_V1_ENV_RST;;n;
P_VCF_V1_VEL_SCALE;;n;
P_VCF_V2_ENV_TYPE;;n;
P_VCF_V2_DELAY;;n;
P_VCF_V2_ATTACK;;n;
P_VCF_V2_DECAY;;n;
P_VCF_V2_SUSTAIN;;n;
P_VCF_V2_HOLD;;n;
P_VCF_V2_RELEASE;;n;
P_VCF_V2_ATT_CURVE;;n;
P_VCF_V2_DEC_CURVE;;n;
P_VCF_V2_SUS_TIME;;n;
P_VCF_V2_REL_CURVE;;n;
P_VCF_V2_ENV_RST;;n;
P_VCF_V2_VEL_SCALE;;n;
P_VCF_DCO_ENV_TYPE;;n;
P_VCF_DCO_DELAY;;n;
P_VCF_DCO_ATTACK;;n;
P_VCF_DCO_DECAY;;n;
P_VCF_DCO_SUSTAIN;;n;
P_VCF_DCO_HOLD;;n;
P_VCF_DCO_RELEASE;;n;
P_VCF_DCO_ATT_CURVE;;n;
P_VCF_DCO_DEC_CURVE;;n;
P_VCF_DCO_SUS_TIME;;n;
P_VCF_DCO_REL_CURVE;;n;
P_VCF_DCO_ENV_RST;;n;
P_VCF_DCO_VEL_SCALE;;n;
P_UNLINK_MODE;;n;
P_ACCEL_LINK;P_ACCEL_SYNC;y;
P_ENV_LINK;P_ENV_SYNC;y;
P_FILTER_LINK;;n;
P_MOD_RATE_LFO1;P_LFO_RATE;v;log2(1.01 + P_LFO_RATE)
P_MOD_RATE_LFO2;P_LFO_RATE;v;log2(1.01 + P_LFO_RATE)
P_MOD_RATE_LFO3;P_LFO_RATE;v;log2(1.01 + P_LFO_RATE)
P_MOD1_SUBDIV_UP;P_LFO_V1_UPPER_SUBDIV;y;
P_MOD1_SUBDIV_DOWN;P_LFO_V1_LOWER_SUBDIV;y;
P_MOD2_SUBDIV_UP;P_LFO_FILT_UPPER_SUBDIV;y;
P_MOD2_SUBDIV_DOWN;P_LFO_FILT_LOWER_SUBDIV;y;
P_MOD3_SUBDIV_UP;P_LFO_V2_UPPER_SUBDIV;y;
P_MOD3_SUBDIV_DOWN;P_LFO_V2_LOWER_SUBDIV;y;
P_MOD1_ACT_SLOT;;n;
P_MOD2_ACT_SLOT;;n;
P_MOD3_ACT_SLOT;;n;
P_MOD_DEPTH_1A;P_LFO_V1;v;* P_LFO_V1_FREQ_AMP
P_MOD_DEPTH_1B;P_LFO_V1;v;* !P_LFO_V1_FREQ_AMP
P_MOD_DEPTH_2A;P_LFO_FC;v;* P_LFO_CF_RES
P_MOD_DEPTH_2B;P_LFO_FC;v;* !P_LFO_CF_RES
P_MOD_DEPTH_3A;P_LFO_V2;v;* P_LFO_V2_FREQ_AMP
P_MOD_DEPTH_3B;P_LFO_V2;v;* !P_LFO_V2_FREQ_AMP
P_MOD1_SHAPE;P_LFO_V1_SHAPE;y;
P_MOD2_SHAPE;P_LFO_FILT_SHAPE;y;
P_MOD3_SHAPE;P_LFO_V2_SHAPE;y;
P_MOD1_SLOPE;;n;
P_MOD2_SLOPE;;n;
P_MOD3_SLOPE;;n;
P_MOD1_X_OFFS_STEP;;n;
P_MOD1_X_OFFS_TRI;P_LFO_TRI_X_OFFSET;y;0.01
P_MOD1_X_OFFS_SQ;P_LFO_SQ_X_OFFSET;y;0.01
P_MOD1_X_OFFS_SAW_UP;P_LFO_SAW_UP_X_OFFSET;y;0.01
P_MOD1_X_OFFS_SAW_DOWN;P_LFO_SAW_DOWN_X_OFFSET;y;0.01
P_MOD1_Y_OFFS_STEP;;n;
P_MOD1_Y_OFFS_TRI;P_LFO_TRI_Y_OFFSET;y;0.01
P_MOD1_Y_OFFS_SQ;P_LFO_SQ_Y_OFFSET;y;0.01
P_MOD1_Y_OFFS_SAW_UP;P_LFO_SAW_UP_Y_OFFSET;y;0.01
P_MOD1_Y_OFFS_SAW_DOWN;P_LFO_SAW_DOWN_Y_OFFSET;y;0.01
P_MOD2_X_OFFS_STEP;;n;
P_MOD2_X_OFFS_TRI;P_LFO_TRI_X_OFFSET;y;0.01
P_MOD2_X_OFFS_SQ;P_LFO_SQ_X_OFFSET;y;0.01
P_MOD2_X_OFFS_SAW_UP;P_LFO_SAW_UP_X_OFFSET;y;0.01
P_MOD2_X_OFFS_SAW_DOWN;P_LFO_SAW_DOWN_X_OFFSET;y;0.01
P_MOD2_Y_OFFS_STEP;;n;
P_MOD2_Y_OFFS_TRI;P_LFO_TRI_Y_OFFSET;y;0.01
P_MOD2_Y_OFFS_SQ;P_LFO_SQ_Y_OFFSET;y;0.01
P_MOD2_Y_OFFS_SAW_UP;P_LFO_SAW_UP_Y_OFFSET;y;0.01
P_MOD2_Y_OFFS_SAW_DOWN;P_LFO_SAW_DOWN_Y_OFFSET;y;0.01
P_MOD3_X_OFFS_STEP;;n;
P_MOD3_X_OFFS_TRI;P_LFO_TRI_X_OFFSET;y;0.01
P_MOD3_X_OFFS_SQ;P_LFO_SQ_X_OFFSET;y;0.01
P_MOD3_X_OFFS_SAW_UP;P_LFO_SAW_UP_X_OFFSET;y;0.01
P_MOD3_X_OFFS_SAW_DOWN;P_LFO_SAW_DOWN_X_OFFSET;y;0.01
P_MOD3_Y_OFFS_STEP;;n;
P_MOD3_Y_OFFS_TRI;P_LFO_TRI_Y_OFFSET;y;0.01
P_MOD3_Y_OFFS_SQ;P_LFO_SQ_Y_OFFSET;y;0.01
P_MOD3_Y_OFFS_SAW_UP;P_LFO_SAW_UP_Y_OFFSET;y;0.01
P_MOD3_Y_OFFS_SAW_DOWN;P_LFO_SAW_DOWN_Y_OFFSET;y;0.01
P_MOD1_CURVE_TRI;P_LFO_TRI_SHAPE_CTRL;y;0.01
P_MOD1_PW_PWM;P_LFO_SQ_SHAPE_CTRL;y;0.01
P_MOD1_CURVE_SAW_UP;P_LFO_SAW_UP_SHAPE_CTRL;y;0.01
P_MOD1_CURVE_SAW_DOWN;P_LFO_SAW_DOWN_SHAPE_CTRL;y;0.01
P_MOD2_CURVE_TRI;P_LFO_TRI_SHAPE_CTRL;y;0.01
P_MOD2_PW_PWM;P_LFO_SQ_SHAPE_CTRL;y;0.01
P_MOD2_CURVE_SAW_UP;P_LFO_SAW_UP_SHAPE_CTRL;y;0.01
P_MOD2_CURVE_SAW_DOWN;P_LFO_SAW_DOWN_SHAPE_CTRL;y;0.01
P_MOD3_CURVE_TRI;P_LFO_TRI_SHAPE_CTRL;y;0.01
P_MOD3_PW_PWM;P_LFO_SQ_SHAPE_CTRL;y;0.01
P_MOD3_CURVE_SAW_UP;P_LFO_SAW_UP_SHAPE_CTRL;y;0.01
P_MOD3_CURVE_SAW_DOWN;P_LFO_SAW_DOWN_SHAPE_CTRL;y;0.01
P_STEP_MOD1_TYPE;P_LFO_STEP_MODE;y;
P_STEP_MOD2_TYPE;P_LFO_STEP_MODE;y;
P_STEP_MOD3_TYPE;P_LFO_STEP_MODE;y;
P_STEP_MOD1_STEPS;P_LFO_STEP_HORIZ_STEPS;y;
P_STEP_MOD2_STEPS;P_LFO_STEP_HORIZ_STEPS;y;
P_STEP_MOD3_STEPS;P_LFO_STEP_HORIZ_STEPS;y;
P_STEP_MOD1_PATT;P_LFO_STEP_RAND_NUMBER;y;
P_STEP_MOD2_PATT;P_LFO_STEP_RAND_NUMBER;y;
P_STEP_MOD3_PATT;P_LFO_STEP_RAND_NUMBER;y;
P_NOISE_MOD1_GRAIN;;n;
P_NOISE_MOD2_GRAIN;;n;
P_NOISE_MOD3_GRAIN;;n;
P_MOD1_ENV_TYPE;;n;
P_MOD1_DELAY;P_LFO_V1_DELAY;y;
P_MOD1_ATTACK;P_LFO_V1_ATTACK;y;
P_MOD1_DECAY;;n;
P_MOD1_SUSTAIN;;n;
P_MOD1_HOLD;;n;
P_MOD1_RELEASE;P_LFO_V1_RELEASE;y;
P_MOD1_ATT_CURVE;;n;
P_MOD1_DEC_CURVE;;n;
P_MOD1_SUS_TIME;;n;
P_MOD1_REL_CURVE;;n;
P_MOD1_ENV_RST;;n;
P_MOD2_ENV_TYPE;;n;
P_MOD2_DELAY;P_LFO_FILT_DELAY;y;
P_MOD2_ATTACK;P_LFO_FILT_ATTACK;y;
P_MOD2_DECAY;;n;
P_MOD2_SUSTAIN;;n;
P_MOD2_HOLD;;n;
P_MOD2_RELEASE;P_LFO_FILT_RELEASE;y;
P_MOD2_ATT_CURVE;;n;
P_MOD2_DEC_CURVE;;n;
P_MOD2_SUS_TIME;;n;
P_MOD2_REL_CURVE;;n;
P_MOD2_ENV_RST;;n;
P_MOD3_ENV_TYPE;;n;
P_MOD3_DELAY;P_LFO_V2_DELAY;y;
P_MOD3_ATTACK;P_LFO_V2_ATTACK;y;
P_MOD3_DECAY;;n;
P_MOD3_SUSTAIN;;n;
P_MOD3_HOLD;;n;
P_MOD3_RELEASE;P_LFO_V2_RELEASE;y;
P_MOD3_ATT_CURVE;;n;
P_MOD3_DEC_CURVE;;n;
P_MOD3_SUS_TIME;;n;
P_MOD3_REL_CURVE;;n;
P_MOD3_ENV_RST;;n;
P_MOD1_DST_A;;n;
P_MOD1_DST_B;;n;
P_MOD2_DST_A;;n;
P_MOD2_DST_B;;n;
P_MOD3_DST_A;;n;
P_MOD3_DST_B;;n;
P_MOD1_DST_PARAM_A;;n;
P_MOD1_DST_PARAM_B;;n;
P_MOD2_DST_PARAM_A;;n;
P_MOD2_DST_PARAM_B;;n;
P_MOD3_DST_PARAM_A;;n;
P_MOD3_DST_PARAM_B;;n;
P_MOD1_CLK_SRC;P_LFO_SYNC;v;0 if 0, 3 if 1, 4 if 2
P_MOD2_CLK_SRC;P_LFO_SYNC;v;1 if 0, 3 if 1, 4 if 2
P_MOD3_CLK_SRC;P_LFO_SYNC;v;2 if 0, 3 if 1, 4 if 2
P_MOD1_PHASE_RST;P_LFO_MOD_RST;y;
P_MOD2_PHASE_RST;P_LFO_MOD_RST;y;
P_MOD3_PHASE_RST;P_LFO_MOD_RST;y;
P_V1_POLY;P_V1_POLY;v;x - 1, clip 0 - 3
P_V2_POLY;P_V2_POLY;v;x - 1, clip 0 - 3
P_DCO_POLY;;n;
P_PWH_R_LOW;P_PWH_R_LOW;y;
P_PWH_R_HI;P_PWH_R_HI;y;
P_MODW_ASSIGN_1;;n;
P_MODW_ASSIGN_2;;n;
P_MODW_ASSIGN_3;;n;
P_MODW_SCALE_1;;n;
P_MODW_SCALE_2;;n;
P_MODW_SCALE_3;;n;
P_CH_PRES_ASSIGN_V1;;n;
P_CH_PRES_ASSIGN_V2;;n;
P_CH_PRES_ASSIGN_DCO;;n;
P_CH_PR_SCALE_V1;;n;
P_CH_PR_SCALE_V2;;n;
P_CH_PR_SCALE_DCO;;n;
P_ARP_V1;P_VOICE1_MODE;y;
P_ARP_V2;P_VOICE2_MODE;y;
P_ARP_DCO;;n;
P_ARP_MODE;P_ARP_MODE;y;
P_ARP_TIME_DIV;P_ARP_TIME_DIV;v;x - 2, clip 0 - 6
P_ARP_GATE_LEN;P_ARP_GATE_LEN;y;
P_ARP_SWING;P_ARP_SWING;v;x/2 + 0.5
P_ARP_NOTE_REPEAT;P_ARP_NOTE_REPEAT;y;
P_ARP_PATTERN_LEN;;n;
P_ARP_ACCENT_PAT;;n;
P_ARP_PATT_SHIFT;;n;
P_ARP_PATT_REP;;n;
P_KEYS_ROOT;;n;
P_KEYS_SCALE;;n;
P_KEYS_STEP;;n;
P_KEY_C_B1;;n;
P_KEY_C_B2;;n;
P_KEY_C_B3;;n;
P_KEY_C_B4;;n;
P_KEY_C_B5;;n;
P_KEY_C_B6;;n;
P_KEY_C_B7;;n;
P_KEY_C_B8;;n;
P_VOC_LSB;;n;
P_VOC_RSB;;n;
P_VOC_Q;;n;
P_VOC_FC;;n;
P_VOC_BLEND;;n;
P_VOC_BW;;n;
P_VOC_FILT_COUNT;;n;
P_VOC_ON;;n;
P_VOC_IN_GAIN;;n;
P_OSC_DRIFT_RATE;;n;
P_DETUNE_CTRL_2;;n;
P_DETUNE_CTRL_3;;n;
P_DETUNE_CTRL_4;;n;
P_CROSS_V1;;n;
P_CROSS_V2;;n;
P_CROSS_DCO;;n;
P_CROSS_PW;;n;
P_CROSS_SCALE;;n;
P_DCO_HARDYNC_STATE;;n;
P_DCO_HARDYNC_TO;;n;
P_TONE_MODE;;n;
P_MAN_TONE_V1;P_V1_TONE;y;0.003921569
P_MAN_TONE_V2;P_V2_TONE;y;0.003921569
P_RND_VOICE_EN;;n;
P_RND_D_WAVE;;n;
P_RND_D_AMP_ENV;;n;
P_RND_D_ACCEL;;n;
P_RND_D_VCF;;n;
P_RND_D_DRIVE;;n;
P_RND_D_NOTES;;n;
P_RND_D_DETUNE;;n;
P_RND_D_MOD1;;n;
P_RND_D_MOD2;;n;
P_RND_D_MOD3;;n;
P_RND_D_MOD_DST;;n;
P_RND_D_CROSS;;n;
P_RND_D_ARP;;n;
P_RND_D_POLY;;n;
P_V1_SUS_BREAK;;n;
P_V2_SUS_BREAK;;n;
P_DCO_SUS_BREAK;;n;
P_VCF_V1_SUS_BREAK;;n;
P_VCF_V2_SUS_BREAK;;n;
P_VCF_DCO_SUS_BREAK;;n;
P_MOD1_SUS_BREAK;;n;
P_MOD2_SUS_BREAK;;n;
P_MOD3_SUS_BREAK;;n;
P_MOD1_VEL_SCALE;;n;
P_MOD2_VEL_SCALE;;n;
P_MOD3_VEL_SCALE;;n;
P_ARP_OCT_RANGE;P_ARP_PATTERN_REPEAT;v;clip -2 - 2
P_DETUNE_EN_V1;;n;
P_DETUNE_EN_V2;;n;
P_DETUNE_EN_DCO;;n;
P_ARP_GLOBAL_ON;P_VOICE1_MODE;v;P_VOICE1_MODE || P_VOICE2_MODE
P_MOD1A_SCALE;;n;
P_MOD2A_SCALE;;n;
P_MOD3A_SCALE;;n;
P_MOD1B_SCALE;;n;
P_MOD2B_SCALE;;n;
P_MOD3B_SCALE;;n;
P_V1_FILT;P_V1_FILT;y;
P_V2_FILT;P_V2_FILT;y;
P_FM_AM;P_FM_AM;v;fm/am
CUSTOM_DATA_STARTS;;n;
ARP_RYTHM_LEN;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
ARP_RYTHM_STEP;;n;
`.trim();